import {
  type ThunkDispatch,
  type UnknownAction,
  createAsyncThunk
} from "@reduxjs/toolkit"

import i18n from "../../../i18n.ts"
import { errorSave } from "../../../redux/errors.slice"
import { type RootState } from "../../../store.ts"
import { type BasePagination } from "../../../types/api.ts"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { unknownError } from "../../../utils/handlers/handle-error"
import { handleTemplateImportError } from "../../../utils/handlers/handle-template-import-error.tsx"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import { serializeData } from "../../../utils/utils"
import { type OperationalState } from "../../Lead/constants/operational-states.ts"
import type { UpdateLeadParams } from "../../Lead/redux/lead.types.ts"
import * as services from "../api/services"
import {
  type ArchiveLeadError,
  type ArchiveLeadErrorDetails,
  type ArchiveLeadParams,
  type BulkLeadActionParams,
  type BulkLeadResult,
  type ChangeStatusLeadParams,
  type ImportLeadsData,
  type InactiveCounts,
  type LeadEmailCampaignRequest,
  type LeadEmailCampaignResult,
  type LeadImportPreview,
  type LeadsStats,
  type TransitionLeadReasonError,
  type TransitionLeadReasonParams
} from "../api/services.types.ts"
import { LEADS_SAVE_RESULT } from "../constants/leads-save-results.ts"
import { readBackwardTransitionConfirmation } from "../helpers/backward-transition-confirmation.ts"
import { readProjectManagerRequired } from "../helpers/project-manager-required.ts"
import { type LeadListItem } from "../types/types.ts"
import { adoptLeadsStats } from "./adopt-leads-stats.ts"
import { deriveLeadsStats } from "./derive-leads-stats.ts"
import { leadsSliceName } from "./leads.constants.ts"
import { leadsSetProp } from "./leads.slice.ts"
import type { LeadsFilterOptions, LeadsFilters } from "./leads.types.ts"

export const fetchLeadsFilterOptions = createAsyncThunk<
  LeadsFilterOptions,
  void
>(
  `${leadsSliceName}/fetchFilterOptions`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getLeadsFilterOptions()
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }
    return response.data as LeadsFilterOptions
  }
)

export const previewImportLeads = createAsyncThunk<LeadImportPreview, File>(
  `${leadsSliceName}/previewImportLeads`,
  async (file, { rejectWithValue }) => {
    const response = await services.previewLeadImport(file)

    // Deliberately silent: the caller decides what the user sees, because an
    // error banner here would be followed by the import's own -- two outcomes
    // for one click. The status travels so it can tell a refused *file* from a
    // check that merely could not run.
    if (!response.ok) {
      return rejectWithValue({
        status: response.status ?? null,
        data: response.data ?? null
      })
    }

    // The backend envelopes POST responses and leaves GETs bare, so the rows
    // arrive under `details.data` here and directly on the wizard's lookup.
    // Unwrapped at the edge so nothing downstream has to know that.
    const body = response.data as
      | LeadImportPreview
      | { details?: { data?: LeadImportPreview } }
    const enveloped = (body as { details?: { data?: LeadImportPreview } })
      ?.details?.data
    return enveloped ?? (body as LeadImportPreview)
  }
)

export const importLeads = createAsyncThunk<ImportLeadsData, File>(
  `${leadsSliceName}/importLeads`,
  async (file, { dispatch, rejectWithValue }) => {
    const response = await services.importLeads(file)

    if (!response.ok) {
      const blob = response.data as Blob
      const text = await blob.text()
      const data = JSON.parse(text)

      dispatch(errorSave(data?.detail || data?.details))
      return rejectWithValue(data || { errors: "Unknown error" })
    }

    const isError = handleTemplateImportError(
      response,
      "import-leads-template.xlsx"
    )

    if (isError) {
      dispatch(
        errorSave("Import completed with errors. Check the downloaded file.")
      )
      return rejectWithValue({ errors: "File with corrections returned" })
    }

    showResponseMessage(response, "Leads imported data successfully.")
    return response.data as ImportLeadsData
  }
)

export interface LeadsExportResult {
  blob: Blob
  /** Rows in the file, and how many leads the filters actually matched. */
  rowCount: number | null
  totalCount: number | null
  truncated: boolean
}

/**
 * The API caps the file and says so in `X-Export-Truncated`. Reading it is the
 * point: a capped export that downloads silently looks like a complete one.
 * The header is only visible because CORS_EXPOSE_HEADERS lists it.
 */
export const exportLeads = createAsyncThunk<
  LeadsExportResult,
  Partial<LeadsFilters> & { ids?: string }
>(
  `${leadsSliceName}/exportLeads`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.exportLeads(params)

    if (!response.ok) {
      dispatch(errorSave("Could not export leads. Please try again."))
      return rejectWithValue({ errors: "Export failed" })
    }

    const header = (name: string) => {
      const raw =
        response.headers?.[name] ?? response.headers?.[name.toLowerCase()]
      return raw == null ? null : String(raw)
    }
    const asNumber = (value: string | null) => {
      const parsed = Number(value)
      return value != null && Number.isFinite(parsed) ? parsed : null
    }

    return {
      blob: response.data as Blob,
      rowCount: asNumber(header("x-export-row-count")),
      totalCount: asNumber(header("x-export-total-count")),
      truncated: header("x-export-truncated") === "true"
    }
  }
)

export const bulkUpdateLeads = createAsyncThunk<
  BulkLeadResult,
  BulkLeadActionParams
>(
  `${leadsSliceName}/bulkUpdateLeads`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.bulkUpdateLeads(params)

    if (!response.ok) {
      // Answered with a prompt, not an error toast — see the helper.
      if (readProjectManagerRequired(response?.data?.details)) {
        return rejectWithValue(response?.data?.details)
      }

      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return unwrapEnvelope<BulkLeadResult>(response.data)
  }
)

export const sendLeadEmailCampaign = createAsyncThunk<
  LeadEmailCampaignResult,
  LeadEmailCampaignRequest
>(
  `${leadsSliceName}/sendLeadEmailCampaign`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.sendLeadEmailCampaign(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return unwrapEnvelope<LeadEmailCampaignResult>(response.data)
  }
)

export const fetchImportLeadsTemplates = createAsyncThunk<Blob>(
  `${leadsSliceName}/fetchImportLeadsTemplates`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getImportLeadsTemplates()

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data as Blob
  }
)

export const fetchLeads = createAsyncThunk<
  BasePagination<LeadListItem>,
  LeadsFilters & { isMore?: boolean }
>(
  `${leadsSliceName}/fetchLeads`,
  // `isMore` tells the reducer whether to append; it is not a query parameter.
  async ({ isMore: _isMore, ...params }, { dispatch, rejectWithValue }) => {
    const response = await services.getLeads(params)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }
    return response.data as BasePagination<LeadListItem>
  }
)

export const fetchInactiveLeads = createAsyncThunk<
  BasePagination<LeadListItem>,
  LeadsFilters & { isMore?: boolean }
>(
  `${leadsSliceName}/fetchInactiveLeads`,
  // `isMore` tells the reducer whether to append; it is not a query parameter.
  async ({ isMore: _isMore, ...params }, { dispatch, rejectWithValue }) => {
    const response = await services.getLeads(params)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }
    return response.data as BasePagination<LeadListItem>
  }
)

export const changeLeadStatus = createAsyncThunk<
  unknown,
  ChangeStatusLeadParams
>(
  `${leadsSliceName}/changeLeadStatus`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.changeStatusLead(params)

    if (!response.ok) {
      const details = response?.data?.details

      // Answered with a prompt, not an error toast — the caller asks for the PM.
      if (readProjectManagerRequired(details)) return rejectWithValue(details)
      // Leaving Won is asked about, not reported — the caller confirms it.
      if (readBackwardTransitionConfirmation(details)) {
        return rejectWithValue(details)
      }

      if (details?.type === LEADS_SAVE_RESULT.MISSING_FIELDS) {
        dispatch(
          leadsSetProp({
            key: "saveResult",
            value: {
              type: LEADS_SAVE_RESULT.MISSING_FIELDS,
              message: details?.message,
              missing: details?.missing,
              fields: details?.fields,
              targetStatus: params?.target_status,
              leadId: params?.id
            }
          })
        )
        return rejectWithValue({})
      } else {
        dispatch(errorSave(details))
        return rejectWithValue(response.data || { errors: "Unknown error" })
      }
    }

    return response?.data
  }
)

interface ChangeLeadOperationalStatesParams {
  id: string
  operational_states: OperationalState[]
}

export const changeLeadOperationalStates = createAsyncThunk<
  unknown,
  ChangeLeadOperationalStatesParams
>(
  `${leadsSliceName}/changeLeadOperationalStates`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.updateLead(params)

    if (!response.ok) {
      type OperationalStatesError = {
        type?: string
        message?: string
        missing?: string[]
      }
      const responseData = response.data as OperationalStatesError
      const details =
        (response.data as { details?: OperationalStatesError })?.details ||
        responseData

      if (details?.type === LEADS_SAVE_RESULT.MISSING_FIELDS) {
        dispatch(
          leadsSetProp({
            key: "saveResult",
            value: {
              type: LEADS_SAVE_RESULT.MISSING_FIELDS,
              message: details.message,
              missing: details.missing,
              leadId: params.id
            }
          })
        )
        return rejectWithValue({})
      }

      dispatch(errorSave(details || unknownError))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data
  }
)

export const archiveLead = createAsyncThunk<unknown, ArchiveLeadParams>(
  `${leadsSliceName}/archiveLead`,
  async (params, { dispatch, getState, rejectWithValue }) => {
    const response = await services.archiveLead(params)

    if (!response.ok) {
      const responseData = response.data as ArchiveLeadError | undefined
      const details: ArchiveLeadErrorDetails | undefined =
        responseData && "details" in responseData
          ? responseData.details
          : (responseData as ArchiveLeadErrorDetails | undefined)

      if (details?.type === LEADS_SAVE_RESULT.MISSING_FIELDS) {
        dispatch(
          leadsSetProp({
            key: "saveResult",
            value: {
              type: LEADS_SAVE_RESULT.MISSING_FIELDS,
              message: details.message,
              missing: details.missing,
              targetStatus: params.target_status,
              leadId: params.id
            }
          })
        )
        return rejectWithValue({})
      }

      // The lead still has meetings in the diary. That is a question for the
      // user, not a failure to report — the modal that asked for the archive
      // shows what would be cancelled and offers to go ahead, so no error
      // toast fires over the top of it.
      if (details?.type === LEADS_SAVE_RESULT.PENDING_COMMITMENTS) {
        return rejectWithValue({
          type: LEADS_SAVE_RESULT.PENDING_COMMITMENTS,
          message: details.message,
          pending_commitments: details.pending_commitments ?? [],
          pending_signatures: details.pending_signatures ?? []
        })
      }

      dispatch(errorSave(details?.message || details?.detail || unknownError))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    const filters = (getState() as RootState).leads.filters
    const serializedFilters = serializeData(filters) as LeadsFilters

    dispatch(fetchLeads(serializedFilters))
    // The tiles take no filters, so the list refetch above leaves them holding
    // a count that still includes the row that just left the Active view.
    dispatch(fetchLeadsStats())
    showResponseMessage(response, i18n.t("leadArchive.success"))

    return response.data
  }
)

export const updateLeadRow = createAsyncThunk<LeadListItem, UpdateLeadParams>(
  `${leadsSliceName}/updateLeadRow`,
  async (params, { dispatch, getState, rejectWithValue }) => {
    const response = await services.updateLead(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    const filters = (getState() as RootState).leads.filters
    const serializeFilters = serializeData(filters) as LeadsFilters

    dispatch(fetchLeads(serializeFilters))

    showResponseMessage(response, "Lead updated successfully.")

    return response.data as LeadListItem
  }
)

/**
 * Re-read the Inactive view after a row leaves it.
 *
 * The badges have to move with the table — a queue the row was counted in is
 * wrong the moment it goes. And removing the last row of the last page leaves
 * the table controlled by a page that no longer exists, so it steps back
 * instead of showing an empty table under a stale pager.
 */
const refreshInactiveAfterRemoval = async ({
  dispatch,
  getState
}: {
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
  getState: () => unknown
}) => {
  const inactiveFilters = (state: unknown) =>
    (state as RootState).leads.inactiveFilters

  const filters = inactiveFilters(getState())
  // Reference-compared below, so serialize a copy: unserialized, `reason`
  // reaches the API as `reason[]` and the queue filter is dropped.
  const queryFilters = serializeData(filters) as LeadsFilters

  dispatch(fetchInactiveCounts({ ...queryFilters }))

  const listed = await dispatch(fetchInactiveLeads({ ...queryFilters }))
  const page = filters?.page || 1

  if (listed.meta.requestStatus !== "fulfilled" || page <= 1) return

  // A fulfilled thunk only means the request succeeded — not that it is still
  // the one on screen. If the user changed queue or search while it was in
  // flight, the reducer already discarded these rows, and stepping the page
  // back would apply this queue's arithmetic to the one they moved to.
  // Untouched branches keep their reference through immer, so an unchanged
  // filters object is the same object.
  if (inactiveFilters(getState()) !== filters) return

  const results = (listed.payload as BasePagination<LeadListItem>)?.results
  if (results && !results.length) {
    dispatch(
      leadsSetProp({ key: "inactiveFilters", value: { page: page - 1 } })
    )
  }
}

export const deleteInactiveLead = createAsyncThunk<{ id: string }, string>(
  `${leadsSliceName}/deleteInactiveLead`,
  async (id, { dispatch, getState, rejectWithValue }) => {
    const response = await services.deleteLead(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    await refreshInactiveAfterRemoval({ dispatch, getState })

    showResponseMessage(response, "Lead deleted successfully.")

    return { ...response.data, id }
  }
)

/**
 * The KPI row and the smart-filter chip counts arrive together: both aggregate
 * over the whole filtered set, so splitting them would double the scan.
 */
// Takes no filters: the tiles are a fixed overview of the whole pipeline, so
// there is nothing about the table's current view to pass along.
export const fetchLeadsStats = createAsyncThunk<LeadsStats, void>(
  `${leadsSliceName}/fetchLeadsStats`,
  async (_arg, { getState, rejectWithValue }) => {
    const response = await services.getLeadsStats()

    if (response.ok) return adoptLeadsStats(response.data as LeadsStats)

    // The endpoint is not implemented yet, so the figures are composed from
    // narrowed list queries meanwhile. Any failure is treated the same way —
    // a derived row beats an empty one, and the derivation reports its own
    // failures per tile rather than throwing.
    try {
      return await deriveLeadsStats({
        userId: (getState() as RootState)?.auth?.userInfo?.id
      })
    } catch {
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }
  }
)

export const fetchInactiveCounts = createAsyncThunk<
  InactiveCounts,
  LeadsFilters
>(
  `${leadsSliceName}/fetchInactiveCounts`,
  async (params, { rejectWithValue }) => {
    // The reason the user is standing in must not narrow its own badge, or
    // every chip would read as the count of the one already selected.
    const { reason: _reason, ...rest } = params
    const response = await services.getInactiveCounts(rest)

    if (!response.ok) {
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data as InactiveCounts
  }
)

export const reactivateLead = createAsyncThunk<unknown, string>(
  `${leadsSliceName}/reactivateLead`,
  async (id, { dispatch, getState, rejectWithValue }) => {
    const response = await services.reactivateLead(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    await refreshInactiveAfterRemoval({ dispatch, getState })
    // The row crosses back into the Active view, so its tiles are wrong too —
    // deleting an already-inactive lead leaves them alone, which is why this
    // sits here rather than in the shared refresh above.
    dispatch(fetchLeadsStats())

    showResponseMessage(response, "Lead reactivated successfully.")

    return response.data
  }
)

/**
 * Moves a lead directly from one inactive reason to another. Subsumes the
 * former single-purpose "move to Do Not Contact" action — one affordance
 * covering every valid target reason instead of a dedicated button per
 * target. The backend decides overlay-swap vs status-rewrite per target; this
 * thunk just forwards the choice.
 */
export const transitionLeadReason = createAsyncThunk<
  unknown,
  TransitionLeadReasonParams
>(
  `${leadsSliceName}/transitionLeadReason`,
  async (params, { dispatch, getState, rejectWithValue }) => {
    const response = await services.transitionLeadReason(params)

    if (!response.ok) {
      const details = (response.data as TransitionLeadReasonError)?.details
      dispatch(errorSave(details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    await refreshInactiveAfterRemoval({ dispatch, getState })

    showResponseMessage(response, "Lead moved successfully.")

    return response.data
  }
)
